import apiClient from './apiClient';

/**
 * User Service
 * Handles all user management related API calls
 */
class UserService {
  /**
   * Get all users
   * @param {object} params - Query parameters (page, limit, search, etc.)
   * @returns {Promise} - List of users
   */
  async getAllUsers(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString ? `/users?${queryString}` : '/users';
      const response = await apiClient.get(endpoint);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user by ID
   * @param {string|number} userId - User ID
   * @returns {Promise} - User details
   */
  async getUserById(userId) {
    try {
      const response = await apiClient.get(`/users/${userId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create new user
   * @param {object} userData - User data
   * @returns {Promise} - Created user
   */
  async createUser(userData) {
    try {
      const response = await apiClient.post('/users', userData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user
   * @param {string|number} userId - User ID
   * @param {object} userData - Updated user data
   * @returns {Promise} - Updated user
   */
  async updateUser(userId, userData) {
    try {
      const response = await apiClient.put(`/users/${userId}`, userData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete user
   * @param {string|number} userId - User ID
   * @returns {Promise} - Delete confirmation
   */
  async deleteUser(userId) {
    try {
      const response = await apiClient.delete(`/users/${userId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user status (active/inactive)
   * @param {string|number} userId - User ID
   * @param {string} status - New status
   * @returns {Promise}
   */
  async updateUserStatus(userId, status) {
    try {
      const response = await apiClient.patch(`/users/${userId}/status`, { status });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user profile
   * @returns {Promise} - Current user profile
   */
  async getProfile() {
    try {
      const response = await apiClient.get('/users/profile');
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user profile
   * @param {object} profileData - Profile data to update
   * @returns {Promise} - Updated profile
   */
  async updateProfile(profileData) {
    try {
      const response = await apiClient.put('/users/profile', profileData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Search users
   * @param {string} query - Search query
   * @returns {Promise} - Search results
   */
  async searchUsers(query) {
    try {
      const response = await apiClient.get(`/users/search?q=${encodeURIComponent(query)}`);
      return response;
    } catch (error) {
      throw error;
    }
  }
}

// Create and export a singleton instance
const userService = new UserService();
export default userService;

